import { getBlock, makeId, type ArticleBody } from "@louise/content";
import { getPostProcedureWaitContext } from "@src/packages/cycles/presenters/postProcedureWait";
import type { TranslationFunction } from "@src/packages/i18n";
import type { Article } from "../components/ArticleSectionCard";

/**
 * Curated reading list for the post-procedure wait. Every id is a real
 * registered content block (no fabricated content); the display tag is a short
 * stage label. Missing blocks are skipped, so the section only ever shows real
 * articles. IUI and OI omit transfer-specific explainers because they are not
 * the right procedure context for those waits.
 */
const TRANSFER_WAIT_READINGS: { id: string; tagKey: string }[] = [
  { id: "new-article-waiting-for-result", tagKey: "tag_wait" },
  { id: "moon-article-implantation-explained", tagKey: "tag_science" },
];

function teaserFrom(subtitle: string | undefined, body: ArticleBody): string {
  const raw = subtitle ?? body.sections?.[0]?.content ?? "";
  return raw.length <= 80 ? raw : `${raw.slice(0, 77)}...`;
}

/** Resolve the curated wait-stage articles into `ArticleSectionCard` items. */
export function getWaitStageReadings(
  t: TranslationFunction,
  cycleType?: string | null,
): Article[] {
  const RC = "homepage.review_concept.wait_extras";
  const context = getPostProcedureWaitContext(cycleType);
  const baseReadings =
    context.contentKey === "iui" || context.contentKey === "oi"
      ? [{ id: "new-article-waiting-for-result", tagKey: "tag_wait" }]
      : TRANSFER_WAIT_READINGS;
  const transferGuideId =
    context.contentKey === "fet"
      ? "new-article-fet-guide"
      : context.contentKey === "iui"
        ? "new-article-iui-guide"
        : context.contentKey === "oi"
          ? null
          : "new-article-embryo-transfer-guide";
  const guideTagKey =
    context.contentKey === "fet"
      ? "tag_fet"
      : context.contentKey === "iui"
        ? "tag_insemination"
        : "tag_transfer";
  return [
    ...baseReadings,
    ...(transferGuideId ? [{ id: transferGuideId, tagKey: guideTagKey }] : []),
  ].flatMap(({ id, tagKey }) => {
    const block = getBlock(makeId(id));
    if (!block || block.kind !== "article") return [];
    // getBlock returns a non-discriminated block, so body stays a union even
    // after the kind guard — narrow it the same way blockToArticle does.
    const body = block.body as ArticleBody;
    return [
      {
        id: block.id,
        title: block.title,
        time: block.readTimeMinutes
          ? t("learn.read_time_minutes", {
              params: { minutes: String(block.readTimeMinutes) },
            })
          : "",
        tag: t(`${RC}.${tagKey}`),
        teaser: teaserFrom(block.subtitle, body),
        body,
        tags: block.tags,
      },
    ];
  });
}
